export type ReferenciaVentaEnProceso = { current: boolean };

export function intentarIniciarVenta(ref: ReferenciaVentaEnProceso): boolean {
  if (ref.current) return false;
  ref.current = true;
  return true;
}

export function finalizarVenta(ref: ReferenciaVentaEnProceso): void {
  ref.current = false;
}

export function crearIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ─── Idempotencia persistente por intento de venta ───────────────────────────
//
// PROBLEMA: bloquear el botón solo evita el doble clic. Con conexión lenta el
// cliente aborta a los N segundos mientras el backend YA confirmó el ticket; el
// vendedor vuelve a darle a imprimir y, con una llave nueva, el backend crea un
// SEGUNDO ticket. La llave debe ser la MISMA en todos los reintentos del mismo
// carrito, para que el guard de idempotencia del backend devuelva el ticket
// original en vez de crear otro.
//
// La llave se persiste en localStorage: si la app se recarga o el navegador se
// cierra a mitad de la venta, el reintento sigue usando la misma llave.

const STORAGE_KEY = 'pos.ventaEnCurso.idempotencyKey.v1';
// Más allá de esta ventana la llave se descarta: el backend ya no la tiene
// (IDEMPOTENCY_RETENTION_HOURS = 72h por defecto) y reusarla no aporta nada.
const TTL_MS = 6 * 60 * 60 * 1000; // 6 h

type LlaveGuardada = { key: string; ts: number; huella: string };

/**
 * Huella estable del contenido de la venta. Debe cambiar si cambia CUALQUIER
 * dato que el backend usa en su fingerprint (sorteo, jugadas, montos, cliente).
 */
export function huellaDeVenta(datos: unknown): string {
  const texto = JSON.stringify(datos ?? null);
  // djb2 — no necesita ser criptográfico, solo detectar que el carrito cambió.
  let h = 5381;
  for (let i = 0; i < texto.length; i++) {
    h = ((h << 5) + h + texto.charCodeAt(i)) | 0;
  }
  return `${(h >>> 0).toString(36)}-${texto.length}`;
}

function leer(): LlaveGuardada | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LlaveGuardada;
    if (!parsed?.key || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Devuelve la llave de idempotencia del intento de venta EN CURSO.
 *
 * Reusa la llave guardada SOLO si la huella del carrito es la misma; así un
 * reintento del mismo carrito deduplica, pero si el vendedor corrigió las
 * jugadas después de un fallo se genera una llave nueva (si no, el backend
 * respondería 409 "Idempotency-Key ya usada con datos distintos").
 */
export function obtenerIdempotencyKeyDeVenta(huella: string): string {
  const guardada = leer();
  if (guardada && guardada.huella === huella) return guardada.key;

  const key = crearIdempotencyKey();
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ key, ts: Date.now(), huella } satisfies LlaveGuardada),
    );
  } catch {
    // Sin localStorage (modo privado) se degrada al comportamiento anterior:
    // llave por intento. El guard del backend sigue cubriendo el doble clic.
  }
  return key;
}

/**
 * Cierra el intento de venta y descarta la llave. Llamar SOLO cuando la venta
 * quedó confirmada (o el vendedor descartó el carrito a propósito). Nunca en un
 * catch de red: ahí es justamente donde hay que conservarla.
 */
export function limpiarIdempotencyKeyDeVenta(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignorar
  }
}
